/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ChargerModule from '../../../components/chargerModule';
import {
  DTOgetOrderDetailedData,
  IProductList,
  ORDER_STATUS,
  ORDER_STATUS_ID_CANCELLED,
  ORDER_STATUS_ID_CONFIRMED_FINANCE,
  SHIPMENT_STATUS_ID
} from '../../../types/orders';
import Tab1General from './Components/Tab1General/Tab1General';
import Tab2Products from './Components/Tab2Products/Tab2Products';
import Tab3Billing from './Components/Tab3Billing/Tab3Billing';
import { getOrderDetailedData, updateOrderStatus } from '../../../services/orders';
import CancelOrderModal from '../Components/CancelOrderModal/CancelOrderModal';
import { UploadPaymentLinkModal } from '../Components';
import { ICountryDetail } from '../../../types/countries';
import { getListOfCountriesByOfferedService } from '../../../utils/getCountries';
import Tab4Address from './Components/Tab4Address/Tab4Address';
import { getOrganizationConfigByOrganizationId } from '../../../services/product-configuration.service';
import { IOrganizationConfig } from '../../../types/product.configuration.types';
import { displayErrorNotification, displaySuccessNotification } from '../../../utils/displayNudosStandardNotifications';
import Tab5OrderStatusLogHistory from './Components/Tab5OrderStatusLogHistory/Tab5OrderStatusLogHistory';
import { IOrderHistory } from '../../../types/suscription/history.types';
import { getOrderHistory, getOrderStatusLogLastUpdate } from '../../../services/suscription/history.service';
import type { IOrderStatusLogLastUpdate } from '../../../types/order-status-log.types';
import { formatShortDateAndHour } from '../../../utils/formatDatesAndHours';
import {
  buildCompleteOrderHistory,
  createLastEventToDisplayFromHistory,
  getLastHistoryEvent,
  updateCalculatedOrderHistoryStatus
} from '../../../utils/history';
import useInvoicesState from 'state/useInvoicesState';
import useResetData from 'hooks/useResetData';
import useCommentsState from '../../../state/useCommentsState';
import { OrderSummaryCard } from './Components/OrderSummaryCard/OrderSummaryCard';
import { Tabs } from 'components/Tabs/Tabs';
import { OrderDetailStatus } from './Components/OrderDetailStatus/OrderDetailStatus';
import { OrderDestinationCountLabel } from './Components/OrderDestinationCountLabel/OrderDestinationCountLabel';
import { BordResponsibleUser } from 'components/NudosComponents';
import { useBordResponsibleUserData } from 'components/NudosComponents/BordReponsibleUser/hooks/useBordResponsibleUserData';
import { OrderCommentsBox } from './Components/OrderCommentsBox/OrderCommentsBox';
import { Breadcrumbs } from 'components/BreadCrumbs/BreadCrumbs';
import { formatDataForOrderSummaryCard } from './utils';
import LastUpdate from 'components/LastUpdate/LastUpdate';
import BordStatus from 'components/NudosComponents/BordStatus/BordStatus';
import { STATUS_VARIANTS } from 'components/NudosComponents/BordStatus/BordStatus.type';
import OrderActionsDropdown from '../Components/OrderActionsDropdown/OrderActionsDropdown';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import ModalConfirmPayment from 'components/modalConfirmPayment';
import ButtonConfirmOrder from './Components/ButtonConfirmOrder/ButtonConfirmOrder';
import CustomerChatOpenButton from 'components/CustomerChatOpenButton/CustomerChatOpenButton';
import CustomerChatLateralModal from 'components/CustomerChatLateralModal/CustomerChatLateralModal';
import { MENTION_TYPES } from 'types/dashboard.types';
import useDetailsInitialTab from 'hooks/useDetailsInitialTab';
import useOpenCustomerChatFromLocation from 'hooks/useOpenCustomerChatFromLocation';
import { isCustomerChatEnabledForOrganization } from 'utils/customerChat.utils';

import './OrderDetailsInternalCard.scss';

const ORDER_STATUS_LOG_CANCELLED_ID = 3;
const ORDER_STATUS_LOG_DELIVERED_IDS = [4, 13];

const getStatusLogVariant = (orderStatusId: number): 'default' | 'informative' | 'success' | 'danger' => {
  if (orderStatusId === ORDER_STATUS_LOG_CANCELLED_ID) return STATUS_VARIANTS.danger;
  if (ORDER_STATUS_LOG_DELIVERED_IDS.includes(orderStatusId)) return STATUS_VARIANTS.success;
  return STATUS_VARIANTS.informative;
};

const SHIPMENTS_TAB_ID = 1;

const OrderDetailInternalCard = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const {
    setOrderId,
    setOrganizationName,
    setPaymentAgreementDate,
    setPaymentAgreementDays,
    setHasActiveCredit,
    setIsPaymentAgreement,
    setInvoiceType
  } = useInvoicesState();
  const { resetOrderDataState } = useResetData();
  const { clearComments, setComments } = useCommentsState();
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState<boolean>(false);
  const [showCancelOrderModal, setShowCancelOrderModal] = useState<boolean>(false);
  const [loadingCancelOrder, setLoadingCancelOrder] = useState<boolean>(false);
  const [orderDetailedData, setOrderDetailedData] = useState<DTOgetOrderDetailedData>();
  const [savedOrganizationConfigs, setSavedOrganizationConfigs] = useState<IOrganizationConfig[]>([]);
  const [orderHistory, setOrderHistory] = useState<IOrderHistory[]>([]);
  const [shouldCombineHistory, setShouldCombineHistory] = useState<boolean>(true);
  const [uploadingCounter, setUploadingCounter] = useState<number>(0);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [temporalUrl, setTemporalUrl] = useState<string>();
  const [listOfCountries, setListOfCountries] = useState<ICountryDetail[]>();
  const [lastUpdateFromStatusLog, setLastUpdateFromStatusLog] = useState<IOrderStatusLogLastUpdate | null>(null);
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState<boolean>(false);
  const [confirmPaymentData, setConfirmPaymentData] = useState<IProductList>();
  const [loadingOpenConfirmPaymentModal, setLoadingOpenConfirmPaymentModal] = useState<boolean>(false);
  const [loadingConfirmPaymentSubmit, setLoadingConfirmPaymentSubmit] = useState<boolean>(false);
  const organizationName = orderDetailedData?.generalData?.orgData?.organizationName || '';
  const organizationId = orderDetailedData?.generalData?.orgData?.organizationId;
  const shimpmentData = orderDetailedData?.generalData?.shipmentData;
  const orderStatusName = orderDetailedData?.statusName as ORDER_STATUS;
  const committedPaymentDate = orderDetailedData?.generalData?.paymentData?.committedPaymentDate || '';
  const organizationCreditDays = orderDetailedData?.generalData?.paymentData?.paymentAgreementDays || 0;
  const hasActiveCredit = orderDetailedData?.hasActiveCredit;
  const hasAnyShipmentDelivered =
    orderDetailedData?.generalData?.shipmentData?.some(s => s.statusId === SHIPMENT_STATUS_ID.DELIVERED) ?? false;
  const isOrderCancelled = orderStatusName === ORDER_STATUS.CANCELLED;

  const { onlyDevelopers } = useOnlyDevelopers();
  const initialTab = useDetailsInitialTab(SHIPMENTS_TAB_ID);
  const shouldOpenCustomerChatFromUrl = useOpenCustomerChatFromLocation();
  const [selectedTabId, setSelectedTabId] = useState<number>(initialTab);
  const [customerChatOpen, setCustomerChatOpen] = useState(false);
  const isCustomerChatEnabled = isCustomerChatEnabledForOrganization(organizationId);

  const infoSideBar = { position: 'order' };
  const headerInfo = { title: 'Órdenes', subtitle: 'Compras- Detalle pedido' };
  const headerDetail = document.getElementById('headerdDetail');

  const updateOrderDetailsInternalCard = () => setUploadingCounter(uploadingCounter + 1);

  const handleChangeSelectedTabId = (newTabId: number) => setSelectedTabId(newTabId);

  const getOrderData = async (desactivateLodader?: boolean) => {
    if (!desactivateLodader) setLoadingData(true);
    try {
      const response = await getOrderDetailedData(orderId);
      if (response) setOrderDetailedData(response);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingData(false);
    }
  };

  const getOrganizationConfig = async () => {
    if (!organizationId) return;
    try {
      const response = await getOrganizationConfigByOrganizationId(organizationId);
      if (response) setSavedOrganizationConfigs(response);
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener las configuraciones almacenadas de la organización</>
      });
    }
  };

  const retrieveOrderHistory = async () => {
    try {
      const response = await getOrderHistory(Number(orderId));
      if (response) {
        setOrderHistory(updateCalculatedOrderHistoryStatus(response));
        setShouldCombineHistory(true);
      }
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener el historial de la orden</>
      });
    }
  };

  const updateWithoutLoaders = async () => {
    const promises: Promise<void>[] = [getOrderData(true), getOrganizationConfig(), retrieveOrderHistory()];
    if (orderId) {
      promises.push(
        getOrderStatusLogLastUpdate(Number(orderId))
          .then(setLastUpdateFromStatusLog)
          .catch(() => setLastUpdateFromStatusLog(null))
      );
    }
    await Promise.all(promises);
  };

  const refetchOrderDetailsWithLoader = async () => {
    const promises: Promise<void>[] = [getOrderData(false), getOrganizationConfig(), retrieveOrderHistory()];
    if (orderId) {
      promises.push(
        getOrderStatusLogLastUpdate(Number(orderId))
          .then(setLastUpdateFromStatusLog)
          .catch(() => setLastUpdateFromStatusLog(null))
      );
    }
    await Promise.all(promises);
  };

  const syncCommentsWithStore = () => {
    if (orderDetailedData?.orderComments) {
      setComments(orderDetailedData.orderComments);
    }
  };

  const changeStatusModalLink = () => setShowPaymentLinkModal(!showPaymentLinkModal);
  const handleCloseCancelOrderModal = () => setShowCancelOrderModal(false);
  const handleOpenCancelOrderModal = () => setShowCancelOrderModal(true);

  const handleConfirmCancelOrder = async () => {
    const currentOrderId = orderDetailedData?.orderId;
    if (!currentOrderId) return;
    setLoadingCancelOrder(true);

    try {
      const formData = new FormData();
      formData.append('body', JSON.stringify({}));

      await updateOrderStatus(currentOrderId, ORDER_STATUS_ID_CANCELLED, formData);

      displaySuccessNotification({
        customJSXMessage: <>Orden N° {currentOrderId} cancelada correctamente</>
      });

      handleCloseCancelOrderModal();
      await updateWithoutLoaders();
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error al cancelar la orden. Por favor intenta de nuevo</>
      });
    } finally {
      setLoadingCancelOrder(false);
    }
  };

  const orderDetailsTabsList = [
    {
      tabId: 0,
      tabName: 'General',
      hidden: true,
      tabComponent:
        orderDetailedData && !!orderDetailedData?.generalData ? (
          <Tab1General
            changeStatusModalLink={changeStatusModalLink}
            updateWithoutLoaders={updateWithoutLoaders}
            updateData={updateOrderDetailsInternalCard}
            updateWithLoaders={refetchOrderDetailsWithLoader}
            orderDetailedData={orderDetailedData}
          />
        ) : (
          <></>
        )
    },
    {
      tabId: 1,
      tabName: 'Envíos',
      tabComponent: (
        <Tab4Address
          orderDetailedData={orderDetailedData}
          updateWithoutLoaders={updateWithoutLoaders}
          updateWithLoaders={refetchOrderDetailsWithLoader}
        />
      )
    },
    {
      tabId: 2,
      tabName: 'Lista productos',
      tabComponent: orderDetailedData ? (
        <Tab2Products
          productsByReferenceList={orderDetailedData?.listProducts}
          organizationName={organizationName}
          organizationId={organizationId}
          savedOrganizationConfigs={savedOrganizationConfigs}
          orderId={orderId}
          updateWithoutLoaders={updateWithoutLoaders}
          orderStatus={orderDetailedData?.statusName}
          selectedTabId={selectedTabId}
          shimpmentData={shimpmentData}
        />
      ) : (
        <></>
      )
    },
    {
      tabId: 3,
      tabName: 'Facturación',
      tabComponent: orderDetailedData ? (
        <Tab3Billing
          updateOrderDetailsInternalCard={updateOrderDetailsInternalCard}
          updateWithoutLoaders={updateWithoutLoaders}
          orderDetailedData={orderDetailedData}
          listOfCountries={listOfCountries || []}
        />
      ) : (
        <></>
      )
    },
    {
      tabId: 4,
      tabName: 'Historial',
      tabComponent: (
        <Tab5OrderStatusLogHistory orderId={orderId} shipmentData={orderDetailedData?.generalData?.shipmentData} />
      )
    }
  ];

  const setOrderData = () => {
    setOrderId(Number(orderId));
    setOrganizationName(organizationName);
    setPaymentAgreementDate(committedPaymentDate);
    setPaymentAgreementDays(organizationCreditDays);
    setHasActiveCredit(hasActiveCredit || false);
    setIsPaymentAgreement(!!committedPaymentDate || false);
    setInvoiceType('order');
  };

  const isOrderStatusChangedDetail = (detail: object): detail is { orderId: number } => {
    const orderIdValue = Object.getOwnPropertyDescriptor(detail, 'orderId')?.value;
    return typeof orderIdValue === 'number';
  };

  const handleOrderStatusChanged = (e: Event) => {
    if (!(e instanceof CustomEvent)) return;
    const { detail } = e;
    if (detail == null || typeof detail !== 'object') return;
    if (!isOrderStatusChangedDetail(detail)) return;
    if (String(detail.orderId) === orderId) updateOrderDetailsInternalCard();
  };

  const summaryCardData = formatDataForOrderSummaryCard(orderDetailedData!);
  const lastEvent = getLastHistoryEvent<IOrderHistory>(orderHistory);
  const lastEventToDisplayFromHistory = createLastEventToDisplayFromHistory(lastEvent);

  const ORDER_STATUS_ID_CANCELLED_LOCAL = 3;
  const lastEventToDisplay = lastUpdateFromStatusLog
    ? {
        title: lastUpdateFromStatusLog.orderStatusName,
        description: lastUpdateFromStatusLog.responsibleUser
          ? `Movido por: ${lastUpdateFromStatusLog.responsibleUser.firstName} ${lastUpdateFromStatusLog.responsibleUser.lastName}`
          : '',
        date: formatShortDateAndHour(lastUpdateFromStatusLog.createdAt)?.textWithTimezone ?? '',
        isProblemActive: lastUpdateFromStatusLog.orderStatusId === ORDER_STATUS_ID_CANCELLED_LOCAL
      }
    : lastEventToDisplayFromHistory;

  const shouldShowConfirmPaymentModal = showConfirmPaymentModal && !!confirmPaymentData;
  const isOrderDetailLoaded = !!orderDetailedData && !loadingData;

  const handleCloseConfirmPaymentModal = () => {
    setShowConfirmPaymentModal(false);
    setConfirmPaymentData(undefined);
  };

  const handleSubmitConfirmPaymentFromModal = async (orderData: IProductList) => {
    const currentOrderId = orderDetailedData?.orderId;
    if (!currentOrderId) return;
    setLoadingConfirmPaymentSubmit(true);

    try {
      const { invoiceNumber, paymentLink, committedPaymentDate: paymentDate, invoice, proofOfPayment } = orderData;
      const formData = new FormData();

      const bodyForm = {
        invoiceNumber,
        paymentLink,
        committedPaymentDate: paymentDate
      };

      formData.append('body', JSON.stringify(bodyForm));

      if (invoice) formData.append('invoice', invoice);
      if (proofOfPayment) formData.append('proofOfPayment', proofOfPayment);

      await updateOrderStatus(Number(currentOrderId), ORDER_STATUS_ID_CONFIRMED_FINANCE, formData);
      displaySuccessNotification({ customJSXMessage: <>La confirmación de la orden fue realizado con éxito</> });
      handleCloseConfirmPaymentModal();
      await updateWithoutLoaders();
      window.dispatchEvent(new CustomEvent('orderStatusChanged', { detail: { orderId: Number(currentOrderId) } }));
    } catch {
      displayErrorNotification({ customJSXMessage: <>Error al confirmar la orden. Por favor intenta de nuevo</> });
    } finally {
      setLoadingConfirmPaymentSubmit(false);
    }
  };

  const orderSummaryCardStatus = (() => {
    if (loadingData) return undefined;

    if (lastUpdateFromStatusLog) {
      return (
        <BordStatus
          variant={getStatusLogVariant(lastUpdateFromStatusLog.orderStatusId)}
          label={lastUpdateFromStatusLog.orderStatusName}
        />
      );
    }

    return <OrderDetailStatus orderData={orderDetailedData} />;
  })();

  const confirmOrderButton = (
    <ButtonConfirmOrder
      orderId={orderId}
      loadingData={loadingData}
      orderStatusName={orderStatusName}
      loadingOpenConfirmPaymentModal={loadingOpenConfirmPaymentModal}
      setLoadingOpenConfirmPaymentModal={setLoadingOpenConfirmPaymentModal}
      setConfirmPaymentData={setConfirmPaymentData}
      setShowConfirmPaymentModal={setShowConfirmPaymentModal}
    />
  );

  useEffect(() => {
    setOrderData();
  }, [orderDetailedData]);

  useEffect(() => {
    getOrganizationConfig();
  }, [organizationId]);

  useEffect(() => {
    getOrderData();
  }, [uploadingCounter]);

  useEffect(() => {
    retrieveOrderHistory();
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    getOrderStatusLogLastUpdate(Number(orderId))
      .then(setLastUpdateFromStatusLog)
      .catch(() => setLastUpdateFromStatusLog(null));
  }, [orderId]);

  useEffect(() => {
    if (!orderDetailedData || !shouldCombineHistory) return;
    setOrderHistory(buildCompleteOrderHistory(orderDetailedData, orderHistory));
    setShouldCombineHistory(false);
  }, [orderDetailedData, orderHistory, shouldCombineHistory]);

  useEffect(() => {
    updateWithoutLoaders();
  }, [resetOrderDataState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    syncCommentsWithStore();
  }, [orderDetailedData?.orderComments]);

  useEffect(() => {
    getListOfCountriesByOfferedService(setListOfCountries);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      clearComments();
    };
  }, [clearComments]);

  useEffect(() => {
    setSelectedTabId(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (shouldOpenCustomerChatFromUrl && isCustomerChatEnabled) setCustomerChatOpen(true);
  }, [shouldOpenCustomerChatFromUrl, isCustomerChatEnabled]);

  const {
    userList,
    responsibleUserList,
    isLoading,
    assignOrderResponsable,
    deleteOrderResponsable,
    getResponsibleUserList
  } = useBordResponsibleUserData(orderDetailedData?.orderId as number);

  useEffect(() => {
    getResponsibleUserList();
  }, [getResponsibleUserList]);

  useEffect(() => {
    window.addEventListener('orderStatusChanged', handleOrderStatusChanged);
    return () => window.removeEventListener('orderStatusChanged', handleOrderStatusChanged);
  }, [orderId]);

  return (
    <div>
      <ChargerModule
        customHeading={
          <Breadcrumbs
            items={[
              { href: '/tracking-order/main', label: 'Órdenes' },
              { href: `/order/details/${orderDetailedData?.orderId}`, label: 'Detalle de la orden' }
            ]}
          />
        }
        infoSideBar={infoSideBar}
        headerInfo={headerInfo}
      >
        <div id="orderDetailsInternalCard" className={onlyDevelopers ? 'paddingTop12' : ''}>
          {showPaymentLinkModal && (
            <UploadPaymentLinkModal
              orderData={{ id: '', orderId: Number(orderDetailedData?.orderId || '0') }}
              defaultLink={temporalUrl || orderDetailedData?.generalData?.paymentData?.paymentLink || ''}
              setShowPaymentLinkModal={setShowPaymentLinkModal}
              handleTemporalUrl={e => setTemporalUrl(e)}
            />
          )}
          {showCancelOrderModal && orderDetailedData && (
            <CancelOrderModal
              showModal={showCancelOrderModal}
              changeShowModal={handleCloseCancelOrderModal}
              onConfirm={handleConfirmCancelOrder}
              loadingConfirmation={loadingCancelOrder}
            />
          )}
          {shouldShowConfirmPaymentModal && confirmPaymentData && (
            <ModalConfirmPayment
              updateData={confirmPaymentData}
              actionUpdate={handleSubmitConfirmPaymentFromModal}
              getGeneralData={handleCloseConfirmPaymentModal}
              loadingUpdate={loadingConfirmPaymentSubmit}
            />
          )}
          <section id="headerdDetail" className="orderDataSection">
            <div className="orderSummaryContainer">
              <OrderSummaryCard
                id={orderId}
                orgData={summaryCardData?.orgData}
                userData={summaryCardData?.userData}
                loading={loadingData}
                status={orderSummaryCardStatus}
                subtitle={loadingData ? undefined : <OrderDestinationCountLabel orderData={orderDetailedData} />}
                responsibleUsers={
                  <BordResponsibleUser
                    bordResponsibleUserSearchDropdownCustomClassName="bordResponsibleUserSearchDropdownRightPosition"
                    addResponsiblesButtonClassName="outlineAddResponsiblesButton"
                    plusButtonIconClassName="outlinedPlusButton"
                    allUsersList={userList}
                    showResponsibleUsersSkeletonLoader={isLoading}
                    responsibleUsersList={responsibleUserList}
                    handleAssignResponsible={assignOrderResponsable}
                    handleDeleteResponsible={deleteOrderResponsable}
                  />
                }
                lastUpdate={lastEventToDisplay ? <LastUpdate lastEvent={lastEventToDisplay} /> : <LastUpdate />}
                orderHistory={orderHistory}
                extraActions={
                  <OrderActionsDropdown
                    onOpenCancelModal={handleOpenCancelOrderModal}
                    isCancelDisabled={orderStatusName === ORDER_STATUS.CANCELLED || hasAnyShipmentDelivered}
                  />
                }
                confirmOrderButton={confirmOrderButton}
              />
            </div>

            <div className="internalDetailsTabsRow">
              <Tabs
                loading={loadingData}
                tabs={orderDetailsTabsList}
                defaultSelectedTabId={SHIPMENTS_TAB_ID}
                selectedTabId={selectedTabId}
                onSelectTab={handleChangeSelectedTabId}
              />
              {isCustomerChatEnabled && isOrderDetailLoaded && (
                <CustomerChatOpenButton
                  onClick={() => setCustomerChatOpen(true)}
                  alignWithOrderTabs
                />
              )}
            </div>
            <OrderCommentsBox
              orderId={orderDetailedData?.orderId as number}
              organizationId={orderDetailedData?.billingData?.organizationId as number}
              responsibleUsersList={responsibleUserList}
              onUploadComment={updateWithoutLoaders}
            />
          </section>

          {isOrderDetailLoaded && (
            <section
              className="tabContent pt-24"
              style={{ height: `calc(100% - ${headerDetail?.clientHeight}px)`, overflow: 'auto' }}
            >
              {orderDetailsTabsList.find(tab => tab.tabId === selectedTabId)?.tabComponent}
            </section>
          )}
          {orderId && isCustomerChatEnabled && (
            <CustomerChatLateralModal
              open={customerChatOpen}
              onClose={() => setCustomerChatOpen(false)}
              mode={MENTION_TYPES.ORDER}
              entityId={Number(orderId)}
              onAfterSend={updateWithoutLoaders}
              isEntityCancelled={isOrderCancelled}
            />
          )}
        </div>
      </ChargerModule>
    </div>
  );
};

export default OrderDetailInternalCard;
